import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import ru from "date-fns/locale/ru";
import { getAllDepartments, getAllGroups, getUsersSummary } from "../../../api/api";
import {
  getSalaryPortalSaldoV2,
  syncFinansistPayrollPayments,
} from "../../../api/timesheetApi";
import { UserContext } from "../../../contexts/CurrentUserContext";
import {
  formatUserDisplayName,
  sortUsersAlphabetically,
} from "../../../utils/sortUsersAlphabetically";
import ReportPeriodPicker, {
  formatApiDate,
  getMonthStart,
  toNoonDate,
} from "../ReportPeriodPicker";
import { buildSaldoReport } from "./buildSaldoReport";
import "./AccountCard3350.css";

export const hasAccountCardAccess = (userData) =>
  Boolean(
    userData?.access_levels?.some((level) => {
      const name = typeof level === "string" ? level : level?.name;
      return name === "АДМС" || name === "ФИН";
    })
  );

const createDefaultPeriod = () => {
  const today = toNoonDate(new Date());
  return {
    mode: "period",
    dateFrom: getMonthStart(today),
    dateTo: today,
  };
};

const formatMoney = (value) =>
  Math.round(value).toLocaleString("ru-RU").replace(/\u00A0/g, " ");

const formatPeriodTitle = (dateFrom, dateTo) =>
  `${format(dateFrom, "dd.MM.yyyy")} — ${format(dateTo, "dd.MM.yyyy")}`;

const formatOnDate = (date) => `На ${format(date, "d MMMM", { locale: ru })}`;

const samePeriod = (left, right) =>
  formatApiDate(left.dateFrom) === formatApiDate(right.dateFrom) &&
  formatApiDate(left.dateTo) === formatApiDate(right.dateTo);

const mapEmployees = (users) =>
  sortUsersAlphabetically(users || []).map((user) => ({
    id: user.id,
    name: formatUserDisplayName(user) || "Без имени",
  }));

const CalendarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <rect x="2.25" y="3.25" width="13.5" height="12.5" rx="2" stroke="#8B939E" strokeWidth="1.4" />
    <path d="M2.25 7.25H15.75" stroke="#8B939E" strokeWidth="1.4" />
    <path d="M6 2.25V4.75M12 2.25V4.75" stroke="#8B939E" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const ChevronIcon = () => (
  <svg className="ac3350-select__chevron" width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">
    <path d="M1 1.5L6 6.5L11 1.5" stroke="#8B939E" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M8 2.5V10.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M5 7.5L8 10.5L11 7.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 13.5H13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

function SelectField({
  label,
  valueLabel,
  options,
  isOpen,
  onToggle,
  onSelect,
  fieldRef,
  disabled = false,
  searchable = false,
}) {
  const searchRef = useRef(null);
  const [query, setQuery] = useState("");
  const normalizedQuery = query.trim().toLowerCase();
  const visibleOptions = normalizedQuery
    ? options.filter(
        (option) =>
          option.id === "all" ||
          option.label.toLowerCase().includes(normalizedQuery)
      )
    : options;

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      return;
    }
    if (searchable) searchRef.current?.focus();
  }, [isOpen, searchable]);

  return (
    <div className={`ac3350-select${isOpen ? " is-open" : ""}`} ref={fieldRef}>
      <button
        type="button"
        className="ac3350-select__trigger"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        disabled={disabled}
      >
        <span className="ac3350-select__text">
          <span className="ac3350-select__label">{label}</span>
          <span className="ac3350-select__value">{valueLabel}</span>
        </span>
        <ChevronIcon />
      </button>
      {isOpen && (
        <div className="ac3350-select__menu">
          {searchable && (
            <input
              ref={searchRef}
              className="ac3350-select__search"
              type="text"
              value={query}
              placeholder="Поиск сотрудника"
              onChange={(event) => setQuery(event.target.value)}
            />
          )}
          <ul role="listbox" aria-label={label}>
            {visibleOptions.map((option) => (
              <li key={option.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={option.selected}
                  className={`ac3350-select__option${option.selected ? " is-selected" : ""}`}
                  onClick={() => onSelect(option.id)}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
          {visibleOptions.length === 0 && (
            <p className="ac3350-select__empty">Ничего не найдено</p>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, hint, highlighted }) {
  return (
    <article className={`ac3350-card${highlighted ? " ac3350-card--closing" : ""}`}>
      <p className="ac3350-card__label">{label}</p>
      <p className="ac3350-card__value">{formatMoney(value)} ₸</p>
      <p className="ac3350-card__hint">{hint}</p>
    </article>
  );
}

function AccountCard3350() {
  const { userData } = useContext(UserContext);
  const canView = hasAccountCardAccess(userData);
  const defaultPeriod = useMemo(() => createDefaultPeriod(), []);
  const departmentRef = useRef(null);
  const groupRef = useRef(null);
  const employeeRef = useRef(null);

  const [period, setPeriod] = useState(defaultPeriod);
  const [departmentId, setDepartmentId] = useState("all");
  const [groupId, setGroupId] = useState("all");
  const [employeeId, setEmployeeId] = useState("all");
  const [openSelect, setOpenSelect] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [groups, setGroups] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [scopedEmployees, setScopedEmployees] = useState([]);
  const [isLoadingDirectory, setIsLoadingDirectory] = useState(true);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [reportError, setReportError] = useState("");
  const [syncMessage, setSyncMessage] = useState("");
  const [report, setReport] = useState(null);
  const requestIdRef = useRef(0);

  const employees =
    departmentId === "all" && groupId === "all" ? allEmployees : scopedEmployees;

  const departmentLabel =
    departmentId === "all"
      ? "Все отделения"
      : departments.find((item) => item.id === departmentId)?.name ||
        "Все отделения";
  const groupLabel =
    groupId === "all"
      ? "Все отделы"
      : groups.find((item) => item.id === groupId)?.name || "Все отделы";
  const employeeLabel = isLoadingEmployees
    ? "Загрузка..."
    : employees.find((item) => item.id === employeeId)?.name || "Все сотрудники";

  const canReset =
    departmentId !== "all" ||
    groupId !== "all" ||
    employeeId !== "all" ||
    !samePeriod(period, defaultPeriod);

  const fetchReport = useCallback(
    async (nextPeriod, nextDepartmentId, nextGroupId, nextEmployeeId, labels) => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      setIsGenerating(true);
      setReportError("");

      try {
        const response = await getSalaryPortalSaldoV2({
          dateFrom: formatApiDate(nextPeriod.dateFrom),
          dateTo: formatApiDate(nextPeriod.dateTo),
          departmentId: nextDepartmentId,
          groupId: nextGroupId,
          userId: nextEmployeeId,
        });
        if (requestId !== requestIdRef.current) return;

        setReport(
          buildSaldoReport({
            dateFrom: nextPeriod.dateFrom,
            dateTo: nextPeriod.dateTo,
            departmentId: nextDepartmentId,
            groupId: nextGroupId,
            employeeId: nextEmployeeId,
            departmentLabel: labels.departmentLabel,
            groupLabel: labels.groupLabel,
            employeeLabel: labels.employeeLabel,
            response,
          })
        );
      } catch (error) {
        console.error("Ошибка при формировании карточки счёта 3350:", error);
        if (requestId !== requestIdRef.current) return;
        setReportError(error.message || "Не удалось сформировать карточку");
      } finally {
        if (requestId === requestIdRef.current) setIsGenerating(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!canView) return undefined;
    let cancelled = false;

    const loadDirectory = async () => {
      try {
        setIsLoadingDirectory(true);
        setLoadError("");
        const [departmentList, summary] = await Promise.all([
          getAllDepartments(),
          getUsersSummary({ isActive: true, pageSize: 5000 }),
        ]);
        if (cancelled) return;

        const nextDepartments = (Array.isArray(departmentList) ? departmentList : [])
          .filter((department) => department.is_active !== false)
          .map((department) => ({
            id: department.id ?? department.department_id,
            name: department.name ?? department.department_name,
          }))
          .filter((department) => department.id != null && department.name)
          .sort((left, right) => left.name.localeCompare(right.name, "ru"));
        const nextEmployees = mapEmployees(summary?.users);

        setDepartments(nextDepartments);
        setAllEmployees(nextEmployees);
      } catch (error) {
        console.error("Ошибка при загрузке справочников карточки 3350:", error);
        if (!cancelled) {
          setLoadError("Не удалось загрузить отделения и сотрудников");
        }
      } finally {
        if (!cancelled) setIsLoadingDirectory(false);
      }
    };

    loadDirectory();
    return () => {
      cancelled = true;
    };
  }, [canView]);

  useEffect(() => {
    if (!canView) return undefined;
    let cancelled = false;

    const loadGroups = async () => {
      try {
        setIsLoadingGroups(true);
        const data = await getAllGroups(departmentId === "all" ? null : departmentId);
        if (cancelled) return;
        setGroups(
          (Array.isArray(data) ? data : [])
            .filter((group) => group?.id != null && group.is_active !== false)
            .map((group) => ({ id: group.id, name: group.name }))
            .sort((left, right) => left.name.localeCompare(right.name, "ru"))
        );
      } catch (error) {
        console.error("Ошибка при загрузке отделов:", error);
        if (!cancelled) setGroups([]);
      } finally {
        if (!cancelled) setIsLoadingGroups(false);
      }
    };

    loadGroups();
    return () => {
      cancelled = true;
    };
  }, [canView, departmentId]);

  useEffect(() => {
    if (departmentId === "all" && groupId === "all") {
      setScopedEmployees([]);
      setIsLoadingEmployees(false);
      return undefined;
    }

    let cancelled = false;

    const loadScopedEmployees = async () => {
      try {
        setIsLoadingEmployees(true);
        if (groupId !== "all") {
          const summary = await getUsersSummary({
            groupId,
            isActive: true,
            pageSize: 1000,
          });
          if (!cancelled) setScopedEmployees(mapEmployees(summary?.users));
          return;
        }

        const departmentGroups = await getAllGroups(departmentId);
        const groupList = (Array.isArray(departmentGroups) ? departmentGroups : []).filter(
          (group) => group?.id != null && group.is_active !== false
        );
        const summaries = await Promise.all(
          groupList.map((group) =>
            getUsersSummary({
              groupId: group.id,
              isActive: true,
              pageSize: 1000,
            }).catch(() => ({ users: [] }))
          )
        );
        if (cancelled) return;

        const byId = new Map();
        summaries.forEach((summary) => {
          mapEmployees(summary?.users).forEach((employee) => {
            byId.set(employee.id, employee);
          });
        });
        setScopedEmployees(
          [...byId.values()].sort((left, right) =>
            left.name.localeCompare(right.name, "ru")
          )
        );
      } catch (error) {
        console.error("Ошибка при загрузке сотрудников:", error);
        if (!cancelled) setScopedEmployees([]);
      } finally {
        if (!cancelled) setIsLoadingEmployees(false);
      }
    };

    loadScopedEmployees();
    return () => {
      cancelled = true;
    };
  }, [departmentId, groupId]);

  useEffect(() => {
    if (!openSelect) return undefined;

    const handlePointerDown = (event) => {
      const node =
        openSelect === "department"
          ? departmentRef.current
          : openSelect === "group"
            ? groupRef.current
            : employeeRef.current;
      if (!node?.contains(event.target)) setOpenSelect(null);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpenSelect(null);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openSelect]);

  const handleDepartmentChange = (nextDepartmentId) => {
    setDepartmentId(nextDepartmentId);
    setGroupId("all");
    setEmployeeId("all");
    setOpenSelect(null);
  };

  const handleGroupChange = (nextGroupId) => {
    setGroupId(nextGroupId);
    setEmployeeId("all");
    setOpenSelect(null);
  };

  const hasFilterSelection =
    departmentId !== "all" || groupId !== "all" || employeeId !== "all";

  const handleReset = () => {
    setPeriod(defaultPeriod);
    setDepartmentId("all");
    setGroupId("all");
    setEmployeeId("all");
    setOpenSelect(null);
    setReport(null);
    setReportError("");
    setSyncMessage("");
  };

  const handleGenerate = () => {
    setOpenSelect(null);
    if (!hasFilterSelection) return;
    fetchReport(period, departmentId, groupId, employeeId, {
      departmentLabel,
      groupLabel,
      employeeLabel,
    });
  };

  const handleSyncAll = async () => {
    const confirmed = window.confirm(
      "Обновить выплаты всех сотрудников за выбранный период? Это может занять несколько минут."
    );
    if (!confirmed) return;

    setIsSyncing(true);
    setSyncMessage("");
    setReportError("");
    try {
      const result = await syncFinansistPayrollPayments({
        dateFrom: formatApiDate(period.dateFrom),
        dateTo: formatApiDate(period.dateTo),
      });
      const fetched = result?.fetched ?? 0;
      const upserted = result?.upserted ?? 0;
      setSyncMessage(`Выплаты обновлены: получено ${fetched}, записано ${upserted}`);
    } catch (error) {
      setReportError(error.message || "Не удалось обновить выплаты");
    } finally {
      setIsSyncing(false);
    }
  };

  const handleExport = () => {
    if (!report) return;

    const lines = [
      ["Сотрудник", "Сальдо на начало", "Начислено", "Выплачено", "Сальдо на конец"].join(";"),
      ...report.rows.map((row) =>
        [row.name, row.opening, row.accrued, row.paid, row.closing].join(";")
      ),
      ["ИТОГО", report.totals.opening, report.totals.accrued, report.totals.paid, report.totals.closing].join(";"),
    ];
    const blob = new Blob([`\uFEFF${lines.join("\n")}`], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "Карточка_счета_3350.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const departmentOptions = [
    { id: "all", label: "Все отделения", selected: departmentId === "all" },
    ...departments.map((department) => ({
      id: department.id,
      label: department.name,
      selected: department.id === departmentId,
    })),
  ];
  const groupOptions = [
    { id: "all", label: "Все отделы", selected: groupId === "all" },
    ...groups.map((group) => ({
      id: group.id,
      label: group.name,
      selected: group.id === groupId,
    })),
  ];
  const employeeOptions = [
    { id: "all", label: "Все сотрудники", selected: employeeId === "all" },
    ...employees.map((employee) => ({
      id: employee.id,
      label: employee.name,
      selected: employee.id === employeeId,
    })),
  ];

  if (userData && !canView) {
    return (
      <section className="ac3350">
        <div className="ac3350-panel">
          <p className="ac3350-empty">
            Раздел доступен только пользователям с уровнем доступа АДМС или ФИН.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="ac3350">
      <div className="ac3350-panel">
        <header className="ac3350-header">
          <div className="ac3350-header__title">
            <div>
              <h2>Карточка счета 3350</h2>
              <p>Расчеты с сотрудниками</p>
            </div>
          </div>
          <button
            type="button"
            className="ac3350-export"
            onClick={handleExport}
            disabled={!report}
          >
            <DownloadIcon />
            Экспорт
          </button>
        </header>

        <div className="ac3350-filters">
          <div className="ac3350-filters__row">
            <div className="ac3350-filters__fields">
              <ReportPeriodPicker
                className="ac3350-period"
                periodOnly
                value={period}
                onChange={setPeriod}
                prefix={
                  <>
                    <CalendarIcon />
                    <span className="ac3350-period__caption">Период:</span>
                  </>
                }
              />
              <SelectField
                label="Отделение"
                valueLabel={isLoadingDirectory ? "Загрузка..." : departmentLabel}
                options={departmentOptions}
                isOpen={openSelect === "department"}
                onToggle={() =>
                  setOpenSelect((current) =>
                    current === "department" ? null : "department"
                  )
                }
                onSelect={handleDepartmentChange}
                fieldRef={departmentRef}
                disabled={isLoadingDirectory}
              />
              <SelectField
                label="Отдел"
                valueLabel={isLoadingGroups ? "Загрузка..." : groupLabel}
                options={groupOptions}
                isOpen={openSelect === "group"}
                onToggle={() =>
                  setOpenSelect((current) =>
                    current === "group" ? null : "group"
                  )
                }
                onSelect={handleGroupChange}
                fieldRef={groupRef}
                disabled={isLoadingDirectory || isLoadingGroups}
                searchable
              />
              <SelectField
                label="Сотрудник"
                valueLabel={isLoadingDirectory ? "Загрузка..." : employeeLabel}
                options={employeeOptions}
                isOpen={openSelect === "employee"}
                onToggle={() =>
                  setOpenSelect((current) =>
                    current === "employee" ? null : "employee"
                  )
                }
                onSelect={(nextEmployeeId) => {
                  setEmployeeId(nextEmployeeId);
                  setOpenSelect(null);
                }}
                fieldRef={employeeRef}
                disabled={isLoadingDirectory || isLoadingEmployees}
                searchable
              />
            </div>
            <button
              type="button"
              className="ac3350-reset"
              onClick={handleReset}
              disabled={!canReset}
            >
              Сбросить все
            </button>
          </div>
          <div className="ac3350-filters__actions">
            <button
              type="button"
              className="ac3350-sync"
              onClick={handleSyncAll}
              disabled={isSyncing || isGenerating}
            >
              {isSyncing ? "Обновление выплат..." : "Обновить все выплаты"}
            </button>
            <button
              type="button"
              className="ac3350-generate"
              onClick={handleGenerate}
              disabled={isGenerating || isSyncing || isLoadingEmployees || !hasFilterSelection}
            >
              {isGenerating ? "Формирование..." : "Сформировать"}
            </button>
          </div>
        </div>

        {loadError && <p className="ac3350-error">{loadError}</p>}
        {reportError && <p className="ac3350-error">{reportError}</p>}
        {syncMessage && <p className="ac3350-sync-note">{syncMessage}</p>}

        {isGenerating && !report ? (
          <p className="ac3350-empty">Формирование карточки...</p>
        ) : !report ? (
          <p className="ac3350-empty">
            Выберите отделение, отдел или сотрудника и нажмите «Сформировать»
          </p>
        ) : report ? (
          <div className="ac3350-result">
            <div className="ac3350-result__head">
              <h3 className="ac3350-result__title">
                {formatPeriodTitle(report.dateFrom, report.dateTo)}
                {" · "}
                {report.departmentLabel}
                {report.groupId !== "all" ? ` · ${report.groupLabel}` : ""}
                {" · "}
                {report.employeeLabel}
              </h3>
              <p className="ac3350-result__stamp">
                <span className="ac3350-result__dot" aria-hidden="true" />
                Сформирован {format(report.generatedAt, "dd.MM.yyyy, HH:mm")}
                {report.paymentsSource === "cache" ? " · выплаты из кэша" : ""}
              </p>
            </div>

            <div className="ac3350-cards">
              <SummaryCard
                label="Сальдо на начало"
                value={report.totals.opening}
                hint={`До ${format(report.dateFrom, "d MMMM", { locale: ru })}`}
              />
              <SummaryCard
                label="Начислено"
                value={report.totals.accrued}
                hint="За месяцы периода"
              />
              <SummaryCard
                label="Выплачено"
                value={report.totals.paid}
                hint={`С ${format(report.dateFrom, "d MMMM", { locale: ru })} по ${format(report.dateTo, "d MMMM", { locale: ru })}`}
              />
              <SummaryCard
                label="Сальдо на конец"
                value={report.totals.closing}
                hint={formatOnDate(report.dateTo)}
                highlighted
              />
            </div>

            {report.rows.length === 0 ? (
              <p className="ac3350-empty">Нет данных за выбранные параметры</p>
            ) : (
            <div className="ac3350-table-wrap">
              <table className="ac3350-table">
                <thead>
                  <tr>
                    <th>Сотрудник</th>
                    <th>Сальдо на начало</th>
                    <th>Начислено</th>
                    <th>Выплачено</th>
                    <th>Сальдо на конец</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.name}</td>
                      <td>₸ {formatMoney(row.opening)}</td>
                      <td>₸ {formatMoney(row.accrued)}</td>
                      <td>₸ {formatMoney(row.paid)}</td>
                      <td>₸ {formatMoney(row.closing)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th>ИТОГО</th>
                    <td>₸ {formatMoney(report.totals.opening)}</td>
                    <td>₸ {formatMoney(report.totals.accrued)}</td>
                    <td>₸ {formatMoney(report.totals.paid)}</td>
                    <td>₸ {formatMoney(report.totals.closing)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default AccountCard3350;
